import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography, Paper } from '@mui/material';
import { useAuth } from 'react-oidc-context';

// This component handles direct redirection to Cognito login/signup
const CognitoRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    // Clear any existing tokens 
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    const redirectToCognito = async () => {
      try {
        // Store the current path so we can return here after login
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem('originalPath', currentPath);
        console.log("Storing original path before redirect:", currentPath);

        // Determine if this is signup or login based on the path
        const isSignup = location.pathname.includes('signup');

        // Use Cognito hosted UI with proper parameters for both login and signup
        if (isSignup) {
          // For Cognito, we need to use their hosted UI with the right parameters
          console.log('Redirecting to Cognito signup page');

          // Get the Cognito domain from environment variables
          const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
          const clientId = process.env.REACT_APP_CLIENT_ID || '';
          const redirectUri = `${window.location.origin}/auth/callback`;

          // Build the signup URL with the necessary parameters
          const signupUrl = `https://${cognitoDomain}/signup?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}`;

          console.log(`Redirecting to signup URL: ${signupUrl}`);
          window.location.href = signupUrl;
        } else {
          // Use the standard OIDC flow for login
          auth.signinRedirect()
            .catch(error => {
              console.error('Error redirecting to login:', error);
              navigate('/');
            });
        }
      } catch (error) {
        console.error('Authentication error:', error);
        navigate('/');
      }
    };

    redirectToCognito();
  }, [location, navigate, auth]);

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: '400px', m: 2, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Redirecting to Secure Authentication
        </Typography>
        <Typography variant="body2">
          Please wait while we connect you to our secure login service...
        </Typography>
      </Paper>
    </Box>
  );
};

export default CognitoRedirect;
